import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search } from 'lucide-react';
import { periodHours, CertaintyBadge } from './DriverHistoryView';

const PeriodLine = ({ p, highlight }) => (
  <div className={`flex flex-wrap items-center justify-between gap-2 text-xs border rounded p-2 ${highlight ? 'border-green-500 bg-green-50' : 'bg-white'}`}
    data-testid={highlight ? 'find-driver-match' : 'find-driver-context'}>
    <div className="flex flex-wrap items-center gap-2">
      {highlight && <span className="px-1.5 py-0.5 rounded bg-green-600 text-white font-medium">Résultat correspondant</span>}
      <span className="text-gray-600">{periodHours(p)}</span>
      <span className="font-semibold">{p.user_name || '—'}</span>
      <span className="text-gray-500">{p.site_name || p.site_id || ''}</span>
      {p.schedule_id && <span className="text-gray-400">mission liée</span>}
      {(p.km_debut != null || p.km_fin != null) && <span className="text-gray-500">{p.km_debut ?? '—'} → {p.km_fin ?? '—'} km</span>}
    </div>
    <div className="flex items-center gap-2">
      <span className="font-mono">{p.plate_snapshot || p.plate_number}</span>
      <span className="text-gray-400">{p.source === 'reconstruction' ? 'reconstruit' : p.source === 'photo_session' ? 'session' : p.source === 'planning' ? 'planning' : 'historique'}</span>
      <CertaintyBadge certainty={p.certainty} />
    </div>
  </div>
);

const FindDriverSearch = ({ sites = [] }) => {
  const [form, setForm] = useState({ plate: '', date: '', time: '', time_from: '', time_to: '', site_id: '' });
  const [mode, setMode] = useState('time');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const params = { plate: form.plate, date: form.date };
      if (mode === 'time' && form.time) params.time = form.time;
      if (mode === 'range') { if (form.time_from) params.time_from = form.time_from; if (form.time_to) params.time_to = form.time_to; }
      if (form.site_id) params.site_id = form.site_id;
      const res = await axios.get(`${API}/fleet/usage/find-driver`, { params, withCredentials: true });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const statusStyle = {
    identifie: 'bg-green-50 border-green-400 text-green-800',
    ambigu: 'bg-amber-50 border-amber-400 text-amber-800',
    candidats: 'bg-amber-50 border-amber-400 text-amber-800',
    introuvable: 'bg-red-50 border-red-300 text-red-700',
  };

  return (
    <Card data-testid="find-driver-search">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Search className="w-4 h-4" /> Rechercher le conducteur à une date (amendes)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div><Label className="text-xs">Plaque sur l'amende *</Label>
            <Input value={form.plate} onChange={(e) => setF('plate', e.target.value.toUpperCase())} placeholder="AB-123-CD" data-testid="find-driver-plate" /></div>
          <div><Label className="text-xs">Date *</Label>
            <Input type="date" value={form.date} onChange={(e) => setF('date', e.target.value)} data-testid="find-driver-date" /></div>
          {mode === 'time' ? (
            <div><Label className="text-xs">Heure précise (Paris)</Label>
              <Input type="time" value={form.time} onChange={(e) => setF('time', e.target.value)} data-testid="find-driver-time" /></div>
          ) : (
            <div className="flex gap-1">
              <div className="flex-1"><Label className="text-xs">De</Label>
                <Input type="time" value={form.time_from} onChange={(e) => setF('time_from', e.target.value)} data-testid="find-driver-time-from" /></div>
              <div className="flex-1"><Label className="text-xs">À</Label>
                <Input type="time" value={form.time_to} onChange={(e) => setF('time_to', e.target.value)} data-testid="find-driver-time-to" /></div>
            </div>
          )}
          <div><Label className="text-xs">Site (facultatif)</Label>
            <select value={form.site_id} onChange={(e) => setF('site_id', e.target.value)}
              className="w-full px-2 py-2 border rounded-md bg-white text-sm" data-testid="find-driver-site">
              <option value="">Tous</option>
              {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
            </select></div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" className="text-xs text-blue-600 underline" data-testid="find-driver-toggle-mode"
            onClick={() => setMode(m => m === 'time' ? 'range' : 'time')}>
            {mode === 'time' ? 'Utiliser une plage horaire' : 'Utiliser une heure précise'}
          </button>
          <Button size="sm" onClick={search} disabled={!form.plate || !form.date || loading} data-testid="find-driver-submit">
            {loading ? 'Recherche...' : 'Rechercher'}
          </Button>
        </div>

        {error && <p className="text-sm text-red-600" data-testid="find-driver-error">{error}</p>}

        {result && (
          <div className="space-y-3" data-testid="find-driver-result">
            <div className={`border rounded-lg p-3 text-sm font-medium ${statusStyle[result.status] || ''}`} data-testid="find-driver-status">
              {result.status === 'identifie' && '✅ '}
              {result.status === 'introuvable' && '❌ '}
              {(result.status === 'ambigu' || result.status === 'candidats') && '⚠️ '}
              {result.message}
              {result.plate_resolution?.via_plate_change && (
                <span className="block text-xs font-normal mt-1">ℹ️ Plaque retrouvée via un changement de plaque (même véhicule).</span>
              )}
            </div>

            {result.matches.length > 0 && (
              <div className="space-y-1.5">
                {result.matches.map(p => <PeriodLine key={p.assignment_id} p={p} highlight />)}
              </div>
            )}

            {result.candidates.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-amber-700">Candidats à vérifier manuellement (aucune attribution automatique) :</p>
                {result.candidates.map((p, i) => <PeriodLine key={p.assignment_id || `cand-${i}`} p={p} />)}
              </div>
            )}

            {result.day_context.length > 0 && (
              <div className="space-y-1.5" data-testid="find-driver-day-context">
                <p className="text-xs font-semibold text-gray-600">Toutes les périodes du véhicule ce jour-là :</p>
                {result.day_context.map(p => <PeriodLine key={`ctx-${p.assignment_id}`} p={p} highlight={p.is_match} />)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FindDriverSearch;
